export function getInitials(name) {
    if (!name) return '?'
    const parts = name.trim().split(/\s+/).filter(Boolean)
    if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    }
    return name.slice(0, 2).toUpperCase()
}

export function formatDate(ts) {
    if (!ts) return '—'
    const d = new Date(typeof ts === 'number' && ts < 1e12 ? ts * 1000 : ts)
    if (Number.isNaN(d.getTime())) return '—'
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export function formatExpire(ts) {
    if (!ts) return '—'
    return formatDate(ts)
}

export function formatScoreDisplay(score) {
    if (!score) return null
    if (score.pending || score.visible === false) return null
    const total = score.score_total != null ? score.score_total
        : (score.total_score != null ? score.total_score : null)
    if (total == null) return null
    const full = score.full_score != null ? score.full_score : 120
    const consolidated = score.consolidated != null ? score.consolidated : Number(total).toFixed(1)
    return { total, full, consolidated, text: `${total} / ${full}` }
}

/** Format quarter code as "2026 Q3". */
export function formatQuarterWindow(code) {
    const raw = String(code || '').trim()
    if (!raw) return '—'
    const match = raw.match(/^(\d{4})\s*Q([1-4])$/i)
    if (match) return `${match[1]} Q${match[2]}`
    return raw
}

const DEFAULT_PREP_URL = 'https://learningfirst.reachable-edu.com'

export function openPrepExternal(url) {
    const target = (url && String(url).trim()) || process.env.VUE_APP_DSA_PREP_URL || DEFAULT_PREP_URL
    if (!target) return false
    window.open(target, '_blank', 'noopener,noreferrer')
    return true
}

export function studentDisplayName(student) {
    if (!student) return ''
    const en = `${student.student_givenName_pinyin || ''} ${student.student_lastName_pinyin || ''}`.trim()
    if (en) return en
    return `${student.student_givenName || ''} ${student.student_lastName || ''}`.trim()
}

export function subjectCategory(project) {
    if (!project) return 'General'
    const line = project.project_product_line || project.dsa_category || project.project_category
    return (line && String(line).trim()) || 'General'
}

export const SUBJECT_CATEGORIES = [
    'Business & Economics',
    'Science & Mathematics',
    'Social Sciences',
    'Humanities',
    'Technology & Engineering',
    'Arts & Design',
]

export function unwrapId(obj) {
    if (!obj) return ''
    if (obj.$id) return obj.$id
    if (obj.id) return obj.id
    return String(obj)
}

export function toTimestampMs(ts) {
    if (ts == null || ts === '') return null
    const n = Number(ts)
    if (Number.isNaN(n)) return null
    return n < 1e12 ? n * 1000 : n
}

function parseQuarterCode(code) {
    const match = String(code || '').match(/^(\d{4})Q(\d)$/)
    return match ? [Number(match[1]), Number(match[2])] : [0, 0]
}

export function sortQuarters(quarters = []) {
    return [...quarters].sort((a, b) => {
        const [ya, qa] = parseQuarterCode(a.quarter_code)
        const [yb, qb] = parseQuarterCode(b.quarter_code)
        return ya !== yb ? ya - yb : qa - qb
    })
}

/** True when now falls within the quarter cycle window (inclusive of full end day). */
export function isWithinCyclePeriod(quarter, now = Date.now()) {
    const start = toTimestampMs(quarter.cycle_period_start)
    const end = toTimestampMs(quarter.cycle_period_end)
    if (start == null || end == null) return false
    return now >= start && now <= end + 86400000 - 1
}

export function isQuarterRegistrationOpen(quarter, now = Date.now()) {
    if (!isWithinCyclePeriod(quarter, now)) return false
    const close = toTimestampMs(quarter.reg_close)
    if (close != null && now > close + 86400000 - 1) return false
    return true
}

function findCurrentQuarterIndex(quarters, now = Date.now()) {
    const sorted = sortQuarters(quarters)
    let idx = sorted.findIndex(q => q.is_current || q.isCurrent)
    if (idx >= 0) return { sorted, idx }
    idx = sorted.findIndex(q => isWithinCyclePeriod(q, now))
    if (idx >= 0) return { sorted, idx }
    idx = sorted.findIndex(q => {
        const start = toTimestampMs(q.cycle_period_start)
        return start != null && now < start
    })
    if (idx >= 0) return { sorted, idx }
    return { sorted, idx: Math.max(0, sorted.length - 1) }
}

/** Current cycle plus the next three quarters for catalog columns. */
export function buildCatalogQuarters(quarters = [], now = Date.now()) {
    const { sorted, idx } = findCurrentQuarterIndex(quarters, now)
    return sorted.slice(idx, idx + 4).map((q) => {
        const isCurrent = !!(q.is_current || q.isCurrent || isWithinCyclePeriod(q, now))
        const registrationOpen = !!(q.registration_open || (isCurrent && isQuarterRegistrationOpen(q, now)))
        return {
            ...q,
            is_current: isCurrent,
            isCurrent,
            registration_open: registrationOpen,
        }
    })
}

export function formatExamHint(quarter) {
    if (quarter.exam_start) {
        return `Assessment: ${formatDate(quarter.exam_start)}`
    }
    return quarter.exam_hint || 'Testing window'
}

export function formatRegistrationHint(quarter, isCurrent, { enrollment = false } = {}) {
    const verb = enrollment ? 'Enrollment' : 'Registration'
    if (isCurrent) {
        if (quarter.reg_close) {
            if (quarter.registration_deadline_planned) {
                return `${verb} closes Planned`
            }
            return `${verb} closes ${formatDate(quarter.reg_close)}`
        }
        return quarter.reg_hint || `${verb} open`
    }
    const openAt = quarter.reg_open || quarter.cycle_period_start
    if (openAt) {
        return `${verb} opens ${formatDate(openAt)}`
    }
    return `${verb} opens soon`
}

/** Normalize quarter list and mark the cycle-active quarter from calendar dates. */
export function resolveCurrentQuarters(quarters = []) {
    const list = sortQuarters(quarters).map(q => ({ ...q }))
    if (!list.length) return list
    const current = buildCatalogQuarters(list)[0]
    const currentCode = current && current.quarter_code
    list.forEach(q => {
        const isCurrent = q.quarter_code === currentCode
        q.is_current = isCurrent
        q.registration_open = isCurrent && isQuarterRegistrationOpen(q)
    })
    return list
}

export function pickCurrentQuarter(quarters = []) {
    const list = resolveCurrentQuarters(quarters)
    return list.find(q => q.is_current) || list[list.length - 1] || null
}

export function applicationStatusLabel(status) {
    const map = {
        待缴费: 'Pending',
        已缴费: 'Paid',
        无需缴费: 'Pass redeemed',
        已取消: 'Cancelled',
        Pending: 'Pending',
        'Pending payment': 'Pending',
    }
    return map[status] || status || '—'
}

export function isPendingPayment(row) {
    if (!row) return false
    if (row.pending_payment) return true
    if (row.application_status === '待缴费') return true
    if (row.status === '待缴费') return true
    if (row.exam_status === 'pending_payment') return true
    const label = String(row.status_label || '').toLowerCase()
    return label === 'pending' || label === 'pending payment'
}

export function examStatusLabel(status) {
    const map = {
        pending_payment: 'Pending',
        registered: 'Registered',
        ready: 'Exam ready',
        completed_pending_score: 'Results pending',
        scored: 'Scored',
    }
    return map[status] || status || '—'
}

export function examStatusChip(status) {
    if (status === 'scored') return 'open'
    if (status === 'ready') return 'soon'
    if (status === 'completed_pending_score' || status === 'pending_payment') return 'pend'
    return 'soon'
}
